import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Avatar,
  Badge,
  Box,
  Button,
  IconButton,
  SkeletonCircle,
  SkeletonText,
  Tooltip,
  useToast,
} from "@chakra-ui/react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import {
  fetchRecommendedJobsAsync,
  selectRecommendedJobs,
} from "./RecommendedJobsSlice/RecommendedJobsSlice";
import { TimeDifference } from "../common/TimeDifference";
import {
  EnvelopeIcon,
  PhoneIcon,
  BuildingOfficeIcon,
  MapPinIcon,
  BriefcaseIcon,
  CurrencyRupeeIcon,
  ClockIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  BookmarkIcon as BookmarkOutline,
} from "@heroicons/react/24/outline";
import {
  CheckCircleIcon,
  UserIcon,
  BookmarkIcon as BookmarkSolid,
} from "@heroicons/react/24/solid";

import {
  fetchBasicDetailsAsync,
  selectBasicDetails,
} from "../profile/BasicDetailsController/BasicDetailsSlice";
import { updateBookmarkAsync } from "../candidateDashboard/candidateSlice/SavedJobs/SavedJobsSlice";
import {
  CANDIDATE_PROFILE,
  EMPLOYER_COMPANY_LOGO_SAVE_URL,
} from "../common/URLs";
import { NoDataFound } from "../../pages/CommonPublicPages/Basic/NoDataFound";
import { fetchUserRole } from "../common/FetchUserRole";

function RecommendedJobs() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const toast = useToast();

  const recommendedJobs = useSelector(selectRecommendedJobs) || [];
  const careerProfile = useSelector(selectBasicDetails);

  const [bookmarkedJobs, setBookmarkedJobs] = useState({});
  const [appliedJobs, setAppliedJobs] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  // UI state: sorting + filters + pagination
  const [sortBy, setSortBy] = useState("relevant"); // 'relevant' | 'recent'
  const [filterBy, setFilterBy] = useState("all"); // 'all' | 'saved' | 'applied'
  const [visibleCount, setVisibleCount] = useState(6);

  const hasFetchedData = useRef(false);
  const ringIdRef = useRef(`ring-${Math.random().toString(36).slice(2)}`);

  // Fetch both jobs and profile at once
  useEffect(() => {
    if (!hasFetchedData.current && (!recommendedJobs || recommendedJobs.length === 0)) {
      hasFetchedData.current = true;
      const fetchData = async () => {
        try {
          setIsLoading(true);
          await Promise.all([
            dispatch(fetchRecommendedJobsAsync()),
            dispatch(fetchBasicDetailsAsync()),
          ]);
        } catch (error) {
          console.error("Error fetching data:", error);
          toast({
            title: "Error loading data",
            description: "Could not fetch all required information",
            status: "error",
            duration: 3000,
            isClosable: true,
            position: "top-right",
          });
        } finally {
          setIsLoading(false);
        }
      };
      fetchData();
    }
  }, [dispatch, toast, recommendedJobs]);

  // Sync applied + saved state from API when jobs change
  useEffect(() => {
    if (Array.isArray(recommendedJobs) && recommendedJobs.length > 0) {
      const initialApplied = {};
      const initialBookmarks = {};
      recommendedJobs.forEach((job) => {
        initialApplied[job.uuid] = job.candidate_job_apply === 1;
        initialBookmarks[job.uuid] = !!job.candidate_job_saved;
      });
      setAppliedJobs(initialApplied);
      setBookmarkedJobs(initialBookmarks);
    }
  }, [recommendedJobs]);

  // Reset visible count when filters/sort change
  useEffect(() => {
    setVisibleCount(6);
  }, [filterBy, sortBy]);

  const handleBookmarkClick = async (jobId) => {
    const role = await new Promise((resolve) => {
      fetchUserRole((r) => resolve(r));
    });

    if (role !== "candidate") {
      toast({
        title: "Please login first",
        description: "You need to login as a candidate to save or apply for a job.",
        status: "warning",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
      navigate("/login");
      return;
    }

    const isCurrentlyBookmarked = !!bookmarkedJobs[jobId];
    const newStatus = isCurrentlyBookmarked ? "remove" : "save";

    // Optimistic UI
    setBookmarkedJobs((prev) => ({ ...prev, [jobId]: !isCurrentlyBookmarked }));

    try {
      await dispatch(updateBookmarkAsync({ job_uuid: jobId, status: newStatus }));
      toast({
        title: `Job ${newStatus === "save" ? "saved" : "removed"} successfully!`,
        status: "success",
        duration: 2000,
        isClosable: true,
        position: "top-right",
      });
    } catch (error) {
      console.error("Failed to update bookmark:", error);
      // Revert on failure
      setBookmarkedJobs((prev) => ({ ...prev, [jobId]: isCurrentlyBookmarked }));
      toast({
        title: "Failed to update bookmark.",
        description: "Something went wrong. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    }
  };

  const calculateProgress = (percentage) => {
    const radius = 50;
    const circumference = 2 * Math.PI * radius;
    const progress =
      (Math.min(Math.max(percentage || 0, 0), 100) / 100) * circumference;
    return `${progress}, ${circumference}`;
  };

  const isNewJob = (created_at) => {
    if (!created_at) return false;
    const created = new Date(created_at);
    const diffDays = (Date.now() - created.getTime()) / (1000 * 60 * 60 * 24);
    return diffDays <= 7; // mark as NEW for 7 days
  };

  // Derived lists
  const filteredJobs = useMemo(() => {
    let list = Array.isArray(recommendedJobs) ? recommendedJobs : [];
    if (filterBy === "saved") {
      list = list.filter((j) => bookmarkedJobs[j.uuid]);
    } else if (filterBy === "applied") {
      list = list.filter((j) => appliedJobs[j.uuid]);
    }
    return list;
  }, [recommendedJobs, filterBy, bookmarkedJobs, appliedJobs]);

  const sortedJobs = useMemo(() => {
    if (sortBy === "recent") {
      return [...filteredJobs].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    }
    // 'relevant' keeps original API order
    return filteredJobs;
  }, [filteredJobs, sortBy]);

  const jobsToShow = useMemo(
    () => sortedJobs.slice(0, visibleCount),
    [sortedJobs, visibleCount]
  );

  return (
    <div className="py-4">
      {/* Header */}
      <div className="mb-4 flex flex-wrap items-center justify-between px-4 md:px-4">
        <div className="px-2 sm:px-4">
          <h3 className="text-2xl font-bold text-gray-800">
            Recommended Jobs For You
          </h3>
          <h6 className="text-gray-600 font-normal mt-1">
            Know your worth and find the job that qualifies your life
          </h6>
          <div className="mt-2 text-sm text-gray-500">
            {sortedJobs.length} match{sortedJobs.length !== 1 ? "es" : ""}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 px-2 sm:px-4">
          {/* Sort control */}
          <div className="bg-gray-100 rounded-lg p-1 flex items-center">
            <button
              onClick={() => setSortBy("relevant")}
              className={`px-3 py-1 rounded-md text-sm transition ${sortBy === "relevant"
                ? "bg-white shadow text-gray-900"
                : "text-gray-600 hover:text-gray-800"
                }`}
            >
              Most relevant
            </button>
            <button
              onClick={() => setSortBy("recent")}
              className={`px-3 py-1 rounded-md text-sm transition ${sortBy === "recent"
                ? "bg-white shadow text-gray-900"
                : "text-gray-600 hover:text-gray-800"
                }`}
            >
              Recent
            </button>
          </div>

          {/* Filter control */}
          <div className="bg-gray-100 rounded-lg p-1 flex items-center">
            <button
              onClick={() => setFilterBy("all")}
              className={`px-3 py-1 rounded-md text-sm transition ${filterBy === "all"
                ? "bg-white shadow text-gray-900"
                : "text-gray-600 hover:text-gray-800"
                }`}
            >
              All
            </button>
            <button
              onClick={() => setFilterBy("saved")}
              className={`px-3 py-1 rounded-md text-sm transition ${filterBy === "saved"
                ? "bg-white shadow text-gray-900"
                : "text-gray-600 hover:text-gray-800"
                }`}
            >
              Saved
            </button>
            <button
              onClick={() => setFilterBy("applied")}
              className={`px-3 py-1 rounded-md text-sm transition ${filterBy === "applied"
                ? "bg-white shadow text-gray-900"
                : "text-gray-600 hover:text-gray-800"
                }`}
            >
              Applied
            </button>
          </div>

          <Link to={"/jobs"}>
            <Button variant="ghost" colorScheme="blue" size="sm">
              Browse all jobs →
            </Button>
          </Link>
        </div>
      </div>

      {/* Content */}
      <div className="w-full flex flex-wrap px-4 md:px-4">
        {/* Profile Section */}
        <div className="w-full md:w-1/2 mb-2 px-1">
          <div className="w-full mt-2 md:mt-0 sticky top-16">
            <div className="bg-white/80 backdrop-blur border border-gray-200 rounded-2xl shadow-sm overflow-hidden w-full">
              {isLoading && !careerProfile ? (
                <Box padding="6" bg="white">
                  <SkeletonCircle size="16" />
                  <SkeletonText
                    mt="4"
                    noOfLines={6}
                    spacing="4"
                    skeletonHeight="3"
                  />
                </Box>
              ) : careerProfile ? (
                <div className="p-6">
                  <div className="flex items-center space-x-4 mb-4">
                    {/* Profile Progress Ring */}
                    <div className="relative w-24 h-24">
                      <svg
                        className="w-full h-full"
                        viewBox="0 0 100 100"
                        role="img"
                        aria-label="Profile completion"
                      >
                        <defs>
                          <linearGradient
                            id={ringIdRef.current}
                            x1="0"
                            y1="0"
                            x2="1"
                            y2="1"
                          >
                            <stop offset="0%" stopColor="#60a5fa" />
                            <stop offset="100%" stopColor="#2563eb" />
                          </linearGradient>
                        </defs>
                        <circle
                          cx="50"
                          cy="50"
                          r="45"
                          fill="none"
                          stroke="#e5e7eb"
                          strokeWidth="6"
                        />
                        <circle
                          cx="50"
                          cy="50"
                          r="45"
                          fill="none"
                          stroke={`url(#${ringIdRef.current})`}
                          strokeWidth="6"
                          strokeLinecap="round"
                          strokeDasharray={calculateProgress(
                            careerProfile.profileCompletion || 0
                          )}
                          transform="rotate(-90 50 50)"
                        />
                      </svg>
                      <div
                        className="absolute inset-0 flex items-center justify-center rounded-full overflow-hidden"
                        style={{
                          top: "12%",
                          left: "12%",
                          right: "12%",
                          bottom: "12%",
                        }}
                      >
                        {careerProfile?.basic_details?.profile_pic ? (
                          <img
                            className="w-full h-full object-cover rounded-full border-2 border-white"
                            src={`${CANDIDATE_PROFILE}${careerProfile?.basic_details?.profile_pic}`}
                            alt="Profile"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center rounded-full bg-gray-100 text-gray-500">
                            <UserIcon className="w-8 h-8" />
                          </div>
                        )}
                      </div>
                      <div className="absolute -bottom-2 left-0 right-0 text-center">
                        <span className="text-xs font-bold text-blue-600 bg-white px-1 rounded">
                          {careerProfile.profileCompletion || 0}%
                        </span>
                      </div>
                    </div>

                    {/* Name + role */}
                    <div className="min-w-0">
                      <div className="text-xl font-bold text-gray-800 truncate">
                        {careerProfile.name}
                      </div>
                      <p className="text-sm text-gray-600 truncate">
                        {careerProfile?.current_company_details?.job_title?.title}
                      </p>
                      {careerProfile?.current_company_details?.current_company && (
                        <p className="text-xs text-gray-500 truncate">
                          @ {careerProfile.current_company_details.current_company}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Contact / quick facts */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                    {careerProfile?.basic_details?.gender && (
                      <Tooltip label="Gender" aria-label="Gender tooltip">
                        <div>
                          <ContactItem
                            icon={UserIcon}
                            text={careerProfile.basic_details.gender}
                          />
                        </div>
                      </Tooltip>
                    )}

                    {careerProfile?.basic_details?.city?.name && (
                      <Tooltip
                        label={`Location: ${careerProfile.basic_details.city.name}, ${careerProfile.basic_details?.state?.name || ""
                          }`}
                        aria-label="Location tooltip"
                      >
                        <div>
                          <ContactItem
                            icon={MapPinIcon}
                            text={`${careerProfile.basic_details.city.name}, ${careerProfile.basic_details?.state?.name || ""
                              }`}
                          />
                        </div>
                      </Tooltip>
                    )}

                    {careerProfile?.phone && (
                      <Tooltip
                        label={`Phone: ${careerProfile.phone}${careerProfile.phone_verify
                          ? " (Verified)"
                          : " (Not Verified)"
                          }`}
                        aria-label="Phone tooltip"
                      >
                        <div className="flex items-center gap-1">
                          <ContactItem
                            icon={PhoneIcon}
                            text={careerProfile.phone}
                          />
                          {careerProfile.phone_verify ? (
                            <CheckCircleIcon className="h-4 w-4 text-green-500" />
                          ) : null}
                        </div>
                      </Tooltip>
                    )}

                    {careerProfile?.email && (
                      <Tooltip
                        label={`Email: ${careerProfile.email}${careerProfile.email_verify
                          ? " (Verified)"
                          : " (Not Verified)"
                          }`}
                        aria-label="Email tooltip"
                      >
                        <div className="flex items-center gap-1">
                          <ContactItem
                            icon={EnvelopeIcon}
                            text={careerProfile.email}
                          />
                          {careerProfile.email_verify ? (
                            <CheckCircleIcon className="h-4 w-4 text-green-500" />
                          ) : null}
                        </div>
                      </Tooltip>
                    )}

                    {careerProfile?.basic_details?.experience_year && (
                      <Tooltip
                        label={`Experience: ${careerProfile.basic_details.experience_year}y ${careerProfile.basic_details.experience_month || 0
                          }m`}
                        aria-label="Experience tooltip"
                      >
                        <div>
                          <ContactItem
                            icon={BriefcaseIcon}
                            text={`${careerProfile.basic_details.experience_year} years${careerProfile.basic_details.experience_month
                              ? `, ${careerProfile.basic_details.experience_month} months`
                              : ""
                              }`}
                          />
                        </div>
                      </Tooltip>
                    )}

                    {careerProfile?.basic_details?.dob && (
                      <Tooltip
                        label={`DOB: ${new Date(
                          careerProfile.basic_details.dob
                        ).toLocaleDateString("en-US", {
                          weekday: "long",
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })}`}
                        aria-label="DOB tooltip"
                      >
                        <div>
                          <ContactItem
                            icon={CalendarIcon}
                            text={new Date(
                              careerProfile.basic_details.dob
                            ).toLocaleDateString()}
                          />
                        </div>
                      </Tooltip>
                    )}

                    {careerProfile?.basic_details?.marital_status && (
                      <Tooltip
                        label="Marital status"
                        aria-label="Marital status tooltip"
                      >
                        <div>
                          <ContactItem
                            icon={UserIcon}
                            text={careerProfile.basic_details.marital_status}
                          />
                        </div>
                      </Tooltip>
                    )}

                    {careerProfile?.current_company_details
                      ?.current_salary_amount && (
                        <Tooltip
                          label={`Current salary: ₹${careerProfile.current_company_details.current_salary_amount} ${careerProfile.current_company_details.current_pay_type
                            }`}
                          aria-label="Salary tooltip"
                        >
                          <div>
                            <ContactItem
                              icon={CurrencyDollarIcon}
                              text={`₹${careerProfile.current_company_details
                                .current_salary_amount
                                } ${careerProfile.current_company_details.current_pay_type?.toLowerCase() ||
                                ""
                                }`}
                            />
                          </div>
                        </Tooltip>
                      )}

                    {careerProfile?.current_company_details?.notice_period && (
                      <Tooltip
                        label={`Notice: ${careerProfile.current_company_details.notice_period
                          }${careerProfile.current_company_details.last_working_date
                            ? ` • LWD: ${new Date(
                              careerProfile.current_company_details
                                .last_working_date
                            ).toLocaleDateString()}`
                            : ""
                          }`}
                        aria-label="Notice period tooltip"
                      >
                        <div>
                          <ContactItem
                            icon={ClockIcon}
                            text={
                              careerProfile.current_company_details.notice_period
                            }
                          />
                        </div>
                      </Tooltip>
                    )}
                  </div>

                  {/* About */}
                  {careerProfile?.basic_details?.profile_summery && (
                    <div className="mt-4">
                      <h2 className="text-sm font-semibold text-gray-800 mb-1">
                        About
                      </h2>
                      <p className="text-xs text-gray-600 leading-relaxed line-clamp-3">
                        {careerProfile?.basic_details?.profile_summery}
                      </p>
                    </div>
                  )}

                  {/* Skills */}
                  {careerProfile?.it_skills &&
                    careerProfile.it_skills.length > 0 && (
                      <div className="mt-4">
                        <h2 className="text-sm font-semibold text-gray-800 mb-1">
                          Technical Skills
                        </h2>
                        <div className="flex flex-wrap gap-1">
                          {careerProfile.it_skills.map((skill) => (
                            <span
                              key={skill.uuid}
                              className="bg-blue-50 text-blue-700 px-2 py-1 rounded-full text-xs font-medium"
                            >
                              {skill?.skill?.title}
                              {skill?.versions && ` v${skill?.versions}`}
                              {skill?.experience_year &&
                                ` (${skill?.experience_year}y)`}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                  {/* Current Employment */}
                  {careerProfile?.current_company_details && (
                    <div className="mt-4">
                      <h2 className="text-sm font-semibold text-gray-800 mb-1">
                        Current Employment
                      </h2>
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <div className="flex items-center space-x-2 mb-2">
                          <BuildingOfficeIcon className="w-4 h-4 text-gray-600" />
                          <span className="text-sm font-medium">
                            {
                              careerProfile.current_company_details
                                .current_company
                            }
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                          <div>
                            Department:{" "}
                            {
                              careerProfile.current_company_details?.department
                                ?.title
                            }
                          </div>
                          <div>
                            Type:{" "}
                            {
                              careerProfile?.current_company_details
                                ?.employement_type
                            }
                          </div>
                          {careerProfile.current_company_details?.job_profile && (
                            <div className="col-span-2">
                              Role:{" "}
                              {
                                careerProfile?.current_company_details
                                  ?.job_profile
                              }
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <Box padding="6" bg="white">
                  <NoDataFound />
                </Box>
              )}
            </div>
          </div>
        </div>

        {/* Jobs Section */}
        <div className="md:w-1/2 w-full px-1">
          <div className="flex flex-col gap-3">
            {/* Loading state */}
            {isLoading && recommendedJobs.length === 0 ? (
              <div className="flex flex-col gap-3">
                {[...Array(3)].map((_, i) => (
                  <Box key={i} padding="6" boxShadow="sm" bg="white" borderRadius="xl">
                    <SkeletonCircle size="10" />
                    <SkeletonText
                      mt="4"
                      noOfLines={4}
                      spacing="4"
                      skeletonHeight="3"
                    />
                  </Box>
                ))}
              </div>
            ) : sortedJobs.length === 0 ? (
              <div className="py-2">
                <NoDataFound
                  title="No Job Listings Found"
                  message="We couldn't find any job listings at the moment. Try switching filters or check back later."
                />
              </div>
            ) : (
              <div>
                {jobsToShow.map((job) => {
                  const isApplied = !!appliedJobs[job.uuid];
                  const isSaved = !!bookmarkedJobs[job.uuid];
                  return (
                    <div
                      key={job.uuid}
                      className="relative bg-white rounded-2xl border border-gray-100 hover:border-blue-200 hover:shadow-lg transition mb-2 group"
                    >
                      {/* Top-right actions */}
                      <div className="absolute top-3 right-3 flex items-center gap-2">
                        {isNewJob(job.created_at) && (
                          <Badge colorScheme="green" variant="subtle" borderRadius="full">
                            NEW
                          </Badge>
                        )}
                        <Tooltip label={isSaved ? "Remove from saved" : "Save job"}>
                          <IconButton
                            size="sm"
                            variant="ghost"
                            aria-label={isSaved ? "Unsave job" : "Save job"}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleBookmarkClick(job.uuid);
                            }}
                            icon={
                              isSaved ? (
                                <BookmarkSolid className="w-5 h-5 text-blue-600" />
                              ) : (
                                <BookmarkOutline className="w-5 h-5 text-gray-600 group-hover:text-gray-800" />
                              )
                            }
                          />
                        </Tooltip>
                      </div>

                      {/* Header (clickable area) */}
                      <Link
                        to={`/jobs/${job.uuid}`}
                        aria-label={`View details for ${job.job_title?.title || "Job"}`}
                      >
                        <div className="flex items-start gap-3 px-4 py-2 border-b border-gray-100">
                          <div className="flex-shrink-0 w-14 h-14 rounded-lg overflow-hidden bg-gray-50">
                            {job?.company_logo ? (
                              <img
                                className="w-full h-full object-cover border rounded-lg"
                                src={`${EMPLOYER_COMPANY_LOGO_SAVE_URL}${job?.company_logo}`}
                                alt={job.company_name}
                                loading="lazy"
                              />
                            ) : (
                              <Avatar name={job.company_name} />
                            )}
                          </div>

                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-lg text-gray-900 truncate">
                              {job.job_title?.title}
                            </div>
                            <p className="text-sm text-blue-600 font-medium truncate">
                              {job.job_name || "N/A"}
                            </p>

                          </div>
                        </div>
                      </Link>

                      {/* Body */}
                      <div className="px-4 py-2 space-y-1">
                        <div className=" flex items-center text-gray-500 text-sm">
                          <BriefcaseIcon className="h-4 w-4 mr-1" />
                          <span className="truncate">{job.company_name}</span>
                        </div>
                        <div className="flex items-center text-gray-600 text-sm">
                          <MapPinIcon className="h-4 w-4 mr-1 flex-shrink-0" />
                          <span className="truncate">
                            {Array.isArray(job.job_locations) &&
                              job.job_locations.map((location, index) => (
                                <span key={location.uuid || index}>
                                  {location.job_location?.title}
                                  {index < job.job_locations.length - 1 ? ", " : ""}
                                </span>
                              ))}
                            {job.state?.name ? `, ${job.state?.name}` : ""}
                          </span>
                        </div>

                        <div className="flex items-center text-gray-600 text-sm">
                          <CurrencyRupeeIcon className="h-4 w-4 mr-1" />
                          <span>
                            {job.salary_min_amount === null ||
                              job.salary_min_amount === 0
                              ? "Not Disclosed"
                              : `₹${job.salary_min_amount || "N/A"} - ₹${job.salary_max_amount || "N/A"
                              } ${job.salary_per_type || "Not Specified"}`}
                          </span>
                        </div>
                      </div>

                      {/* Footer actions */}
                      <div className="px-4 py-3 flex items-center justify-between ">
                        <div className="flex items-center text-gray-500 text-sm">
                          <ClockIcon className="h-4 w-4 mr-1" />
                          <span>
                            <TimeDifference dateTimeString={job.created_at} />
                          </span>
                        </div>

                        {isApplied ? (
                          <span className="inline-flex items-center gap-1 text-green-600 font-semibold text-sm">
                            <CheckCircleIcon className="h-5 w-5" />
                            Applied
                          </span>
                        ) : (
                          <Button size="sm" colorScheme="blue">
                            <Link to={`/jobs/${job.uuid}`}>Apply Now</Link>
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}

                {/* Show more */}
                {sortedJobs.length > visibleCount && (
                  <div className="flex justify-center mt-3">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setVisibleCount((c) => c + 6)}
                    >
                      Show more
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const ContactItem = ({ icon: Icon, text }) => (
  <div className="flex items-center space-x-2 text-sm text-gray-600">
    <Icon className="h-4 w-4 text-gray-400" />
    <span className="truncate">{text}</span>
  </div>
);

export default RecommendedJobs;